import React, { useState } from "react";
// ⚠️ THE EDITABLE SCREEN'S STYLESHEET, ON PURPOSE. Every `yop-*` class below
// comes from it, and none of those rules are nested under that screen's
// `#year-one-plan` id, so they are reusable by name. (Checked before relying on
// it — the ancestor-scoped trap is real elsewhere in this codebase. The id rules
// that DO exist there only set the bounded-height shell, and that id is absent
// wherever this component is used outside its own screen.)
//
// The relative path is the same in both repos: `_shared/` → `..` is
// ExecutionBlueprintLiveReview, `../..` is ExecutionBlueprint, and the Year 1
// folder sits beside it in each app.
import "../../ExecutionBlueprintYearOnePlan/YearOnePlan.scss";

// ═════════════════════════════════════════════════════════════════════════════
// The Year 1 Plan's section list — the accordion of WIGs, its collapsed
// summaries, and its rows.
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/YearOnePlanSections.js
//   10x-frontend/src/components/UserPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/YearOnePlanSections.js
//
// `diff` is the test; change one copy, change both in the same pass.
//
// ── Why this was extracted (2026-09-03) ─────────────────────────────────────
// The Year 1 Plan is drawn in THREE places: the live screen in the coach app,
// the live screen in the CEO app, and the locked view's Year 1 tab (which is
// itself one file shared by both apps). The redesign landed on the live screens
// first, and the instruction that followed was "we also need to same design on
// /locked/year-one screen which we are showing."
//
// Restyling the locked tab's own `lbv-year-*` markup to MATCH would have meant
// four copies of one design held in step by hand — and the locked tab had
// already drifted once, which is how it ended up with a flat list and a
// `(count)` heading while the live screen had accordions. So the markup moved
// here instead and all three call sites render it.
//
// ── What each caller supplies, and why it is a render prop ──────────────────
// The row's two right-hand cells are the ONLY thing that differs between live
// and locked, and they differ in ways this file must not know about:
//
//   `renderControl` → a live `ShortlistButton` that emits `set_item_shortlist`,
//                     or a static `ShortlistTag`. One needs the socket store.
//   `renderOwner`   → a live `OwnerCell` with a roster picker, fixed-position
//                     menu and its own placement maths, or a name and an
//                     avatar. One needs the roster and open-menu state.
//
// Both of those live in their caller. Passing them in keeps this file free of
// the socket, the roster, routing and every app-specific icon path — which is
// what lets it be byte-identical across the two repos AND reusable by a third
// screen that is already shared between them.
//
// The `collapsed` state is owned HERE rather than lifted, because all three
// callers want exactly the same behaviour from it and none of them reads it. It
// is seeded by `startCollapsed` because the two contexts want opposite
// defaults — see that prop.
// ═════════════════════════════════════════════════════════════════════════════

// ⚠️ Inlined rather than imported, and this is the same call `YearOnePlanLoading`
// and `LockedBlueprintView` both make: the two apps keep their icon modules at
// DIFFERENT paths (`StrategyCanvas/StrategyCanvasHelpers` in the coach app,
// `ExecutionBlueprintLiveReview/_design/icons` in the CEO app), so importing a
// shared chevron is the one thing that would stop this file being byte-identical
// across the two repos.
//
// Drawn pointing UP, matching the coach app's `ChevronUpIcon` that the live
// screen used before this extraction — `.yop-group-head.is-collapsed` rotates it
// 180° to point down when shut, so the up-facing path is what the stylesheet
// expects.
const SectionChevron = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 10.5 8 5.5l5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── What a section says while it is shut ─────────────────────────────────────
// One number: how much of this section made it into the plan.
//
// ⚠️ THE FACES AND THE DOTS WERE REMOVED (2026-09-03, on instruction: "remove
// three dots and name badge from accordion"). The header used to carry three
// things — the count, a strip of owner initials, and one dot per row in list
// order — each answering a different question at a different glance distance.
//
// What that reasoning missed is that two of the three answered questions the
// reader had not asked yet. The dots encoded "which ones?" positionally, which
// cannot be read without first learning the encoding, and the faces named
// people whose rows are one click away. Both restated, in symbols, what opening
// the section says in words — and they sat where a section title belongs, so
// the header read as a dashboard rather than a heading.
//
// The count stays because it is the one fact a shut section genuinely hides:
// how many of its WIGs are in the plan.
//
// A section with nothing chosen says so in words rather than showing "0 of 3":
// an empty section is the thing most worth noticing before locking, and a zero
// reads as a value while "None shortlisted" reads as a gap.
//
// The locked tab renders no summary at all (`showSummary={false}`) — see the
// prop's note below.
export const GroupSummary = ({ rows }) => {
  const picked = rows.filter((row) => row.shortlisted);

  return (
    <span className="yop-group-sum">
      {picked.length === 0 ? (
        <span className="yop-group-count is-none">None prioritised</span>
      ) : (
        <span className="yop-group-count">
          <b>{picked.length}</b> of {rows.length} prioritised
        </span>
      )}
    </span>
  );
};

/**
 * The Year 1 Plan's sections, as collapsible cards.
 *
 * @param groups         From `buildYearOneGroups` — `[{ key, label, rows }]`.
 * @param renderControl  `(row) => node` for the row's shortlist cell. Live
 *                       screens pass a button that emits; the locked view
 *                       passes a static tag.
 * @param renderOwner    `(row) => node` for the row's owner cell. Live screens
 *                       pass the roster picker; the locked view passes a name.
 * @param isPending      Optional `(row) => boolean`. Dims a row with an emit in
 *                       flight. The locked view has no emits and omits it.
 * @param filterRows     Optional `(row) => boolean`. Which rows to DRAW.
 *
 *                       ⚠️ IT DOES NOT NARROW THE SUMMARY. The locked view
 *                       passes `(row) => row.shortlisted` because a locked
 *                       record lists only what was chosen (on instruction:
 *                       "only shortlisted will show on locked screen"), and
 *                       `GroupSummary` still counts `group.rows` entire.
 *
 *                       That asymmetry is moot on the locked tab today, which
 *                       renders no summary at all — but it must stay correct
 *                       for any future caller that filters rows AND keeps the
 *                       count. Filter both and the denominator collapses to
 *                       "2 of 2": true of the rows on screen, and a lie about
 *                       the blueprint.
 *
 *                       A section left with nothing to draw is skipped whole,
 *                       so a locked record has no empty cards.
 * @param showSummary    Whether the header carries `GroupSummary` — the
 *                       "n of m shortlisted" count — at all.
 *
 *                       Off on the locked tab, on instruction (2026-09-03):
 *                       that tab IS the shortlist, so a per-section
 *                       restatement of what is in it describes the page back
 *                       to itself, and its sections open expanded anyway.
 *
 *                       ⚠️ IT ALSO REPLACED `showTotals`, which used to drop
 *                       the count and the dot strip while keeping the owner
 *                       faces. Once the faces and dots were removed from the
 *                       header entirely (see `GroupSummary`), the summary was
 *                       nothing BUT the count — so the two props said the same
 *                       thing and one of them had to go.
 * @param startCollapsed Whether sections begin shut.
 *
 *                       ⚠️ NOW FALSE EVERYWHERE (2026-09-03, on instruction:
 *                       every accordion open by default on the read-only
 *                       screen). The locked tab used to pass this, on the
 *                       argument that a record is read rather than worked
 *                       through, so its header summaries could serve as an
 *                       overview and you would expand only what you wanted.
 *
 *                       That argument died with the summaries. Once the locked
 *                       header is just a section name (`showSummary={false}`),
 *                       a shut section says nothing at all — it is four
 *                       labelled lids over the only content on the page. So
 *                       the record now opens fully expanded, and the prop is
 *                       kept rather than deleted because a future caller may
 *                       still want the shut default.
 */
const YearOnePlanSections = ({
  groups,
  renderControl,
  renderOwner,
  isPending,
  filterRows,
  showSummary = true,
  startCollapsed = false,
  // ── Add / remove a WIG (handoff #25 §2, 2026-09-14) ──────────────────────
  // ⚠️ RENDER PROPS, AND THAT IS THE WHOLE DESIGN OF THEM. This file is also
  // what draws the Year 1 tab of `LockedBlueprintView` — the locked, read-only
  // record — so anything rendered unconditionally here appears on a blueprint
  // that can no longer be changed at all.
  //
  // Passing the affordances IN means the locked caller simply omits them and
  // gets exactly what it got before. A `locked` prop would have been the other
  // option and is worse: it puts the decision in this file, where the next
  // person adding a control has to remember to check it, instead of at the two
  // call sites that actually know which screen they are.
  //
  // `renderAddRow(group)` is rendered as the group's last child;
  // `renderRowActions(row, group)` sits at the end of each row.
  //
  // ⚠️ `renderRowActions` gets the GROUP as well as the row, and it needs it:
  // whether a WIG may be removed depends on how many its section still has,
  // and the row model deliberately carries no back-reference to its section.
  // Passing it here beats adding one — `buildYearOneGroups` builds rows for
  // three different screens, and a field only this one needs would be dead
  // weight in the other two.
  renderAddRow,
  renderRowActions,
}) => {
  // Keyed by `group.key`, absent meaning "as the default says". Written as a
  // lazy initialiser rather than a `useEffect` seed so the first paint is
  // already right — a locked record that flashed open and then shut would read
  // as a bug.
  const [collapsed, setCollapsed] = useState(() =>
    startCollapsed
      ? groups.reduce((acc, group) => ({ ...acc, [group.key]: true }), {})
      : {},
  );

  return groups.map((group) => {
    const isCollapsed = !!collapsed[group.key];
    // ⚠️ `visible` for the body, `group.rows` for the summary — see `filterRows`.
    // Nothing below may reach for `visible` when computing a count or a dot.
    const visible = filterRows ? group.rows.filter(filterRows) : group.rows;
    if (visible.length === 0 && filterRows) return null;
    return (
      // ⚠️ A CARD now, where this was a bare text button over borderless rows.
      // It is what lets a shut section be a summary rather than a heading:
      // `GroupSummary` needs a right-hand edge to sit against, and four stacked
      // cards read as four parts of one plan where four heading-and-rule blocks
      // read as a document.
      <div
        className={`yop-group${isCollapsed ? "" : " is-open"}`}
        key={group.key}
      >
        <button
          type="button"
          className={`yop-group-head${isCollapsed ? " is-collapsed" : ""}`}
          onClick={() =>
            setCollapsed((prev) => ({
              ...prev,
              [group.key]: !prev[group.key],
            }))
          }
          aria-expanded={!isCollapsed}
        >
          <span className="yop-group-chevron" aria-hidden="true">
            <SectionChevron />
          </span>
          {/* ⚠️ NO "(n)" AFTER THE LABEL — removed on 2026-09-03, on
              instruction ("we do not need count beside the wig so we can also
              remove that"). The total is now the denominator inside
              `GroupSummary`'s "2 of 3 shortlisted", so it was being printed
              twice on one line, the first time without saying what it
              counted. */}
          <span className="yop-group-name">{group.label}</span>
          {/* ⚠️ `group.rows`, NOT `visible` — the count reads against every WIG
              in the section even when the body is filtered. See `filterRows`.
              (`showSummary` is what a filtered caller turns OFF; it must not be
              faked by narrowing this list, or the "None shortlisted" branch
              would fire on a section whose rows were merely hidden.) */}
          {showSummary && <GroupSummary rows={group.rows} />}
        </button>

        {!isCollapsed &&
          visible.map((row) => (
            <div
              key={row.id}
              // `has-actions` adds the grid's fourth column — see the rule in
              // YearOnePlan.scss. Keyed off the render prop rather than off
              // what it returns, so the track is stable down the list: a
              // section at its floor returns null for every row, and a column
              // that came and went per row would misalign the owners.
              className={`yop-row${row.shortlisted ? " is-selected" : ""}${
                isPending && isPending(row) ? " is-pending" : ""
              }${renderRowActions ? " has-actions" : ""}`}
            >
              {/* ⚠️ `value`, not `label` — the WIG's own text. `label` is the
                  item's category tag (the agent's, or USER_ADDED_LABEL), which
                  is not what either party is choosing between.

                  ⚠️ IT WRAPS, and carries no `title`. This was a
                  `white-space: nowrap` ellipsis cell with the full text on
                  hover — which meant the one thing the reader is deciding about
                  was the one thing they could not read. A WIG is a full sentence
                  with a number and a date in it ("Reach $2M ARR by Dec 2029");
                  truncating it hid exactly the part that distinguishes it from
                  its neighbours. Wrapping costs a second line on the longest
                  rows and is worth it.

                  ⚠️ NO PRIORITY NUMBER either (2026-09-03, on instruction).
                  `row.priority` is still derived by the shared module and is
                  simply not drawn: it was never a server field, only this row's
                  position rendered as if it ranked something. */}
              <span className="yop-row-label">{row.value}</span>
              {renderControl(row)}
              {renderOwner(row)}
              {renderRowActions ? renderRowActions(row, group) : null}
            </div>
          ))}

        {/* Last child of the group, and only when the group is open — an add
            row under a collapsed section would be an invitation to put
            something into a list you cannot see. */}
        {!isCollapsed && renderAddRow ? (
          <div className="yop-group-add">{renderAddRow(group)}</div>
        ) : null}
      </div>
    );
  });
};

export default YearOnePlanSections;
